import Highcharts from "highcharts";
import { HighchartsOptionsUtil } from "app/utils/highchartsOptionsUtil";
import DashboardCard from "./DashboardCard";
import { DashboardModuleProps } from "app/model/db/dashboardModuleProps";
import DashboardLoading from "app/pages/db/modules/DashboardLoading";
import { useEffect, useRef, useState } from "react";
import HighchartsReact from "highcharts-react-official";
import { ImplwayService } from "app/services/rp/ImplWayService";
import addStockChartModule from "highcharts/modules/stock";
import { ResponseResultCode } from "app/model/response-api";
import { SmpModel } from "app/model/monitoring/smpModel";
import dayjs from "dayjs";
import { Constant } from "app/constants/constant";
import { numberFormatter } from "app/utils/stringUtil";

const defaultOption: Highcharts.Options = {
  colors: ['#3c92ff', '#2bd7f6', '#16e4c6'],
  chart: {
    type: 'line',
    height: 350,
    backgroundColor: 'transparent',
    marginLeft: 60,
  },
  time: HighchartsOptionsUtil.locale.kr.time,
  rangeSelector: {
    x: 15,
    buttons: [
      { type: 'day', count: 7, text: '7일' },
      { type: 'month', count: 1, text: '1개월' },
      { type: 'month', count: 3, text: '3개월' },
      { type: 'year', count: 1, text: '1년' },
      { type: 'ytd', text: ' 올해' },
      { type: 'all', text: '전체' }
    ],
    inputEnabled: false,
  },
  tooltip: {
    split: false,
    shared: true,
    useHTML: true,
    formatter(this: any) {
      let s = `<div style="text-align:left;"><b>${dayjs(this.key).format(Constant.FORMAT_YMD_KR)}</b>`;
      this.points?.forEach((f: any) => {
        s += `
          <br />
          <span style="color:${f.color};">●&nbsp;</span>
          <span style="font-weight:500;">${f.series.name}:&nbsp;</span>
          <span style="font-weight:700;">${numberFormatter(f.y, 2)}</span> 원/REC
        `;
      });
      s += '</div>';
      return s;
    }
  },
  legend: {
    enabled: true,
    floating: true,
    symbolWidth: 10,
    symbolHeight: 10,
    align: 'right',
    verticalAlign: 'top',
    y: -42,
    itemStyle: {
      color: '#505050',
      fontSize: '14px'
    },
    itemHoverStyle: {
      color: '#000',
      fontSize: '14px'
    }
  },
  plotOptions: {
    line: {
      marker: {
        enabled: false,
      }
    }
  },
  navigator: {
    xAxis: {
      dateTimeLabelFormats: HighchartsOptionsUtil.locale.kr.dateTimeLabelFormats
    }
  },
  xAxis: {
    type: 'datetime',
    dateTimeLabelFormats: HighchartsOptionsUtil.locale.kr.dateTimeLabelFormats,
    showFirstLabel: true,
    showLastLabel: true,
    labels: {
      style: {
        color: '#555',
        fontSize: '14px'
      }
    }
  },
  yAxis: {
    opposite: false,
    showLastLabel: true,
    title: {
      text: '(원/REC)',
      style: {
        color: '#555',
        fontSize: '12px'
      },
      align: 'high',
      rotation: 0,
      offset: 0,
      y: -25,
      x: -5
    },
    labels: {
      y: 4,
      distance: 7,
      style: {
        color: '#555',
        fontSize: '14px'
      },
    }
  },
  series: []
};

interface SmpData {
  trdYmd: string;
  areaCd: number;
  avgSmp: number;
}

export default function SmpStatistics(props: Readonly<DashboardModuleProps>) {
  Highcharts.setOptions({
    ...HighchartsOptionsUtil.locale.kr.globalOptions,
    title: { text: '' },
    exporting: { enabled: false },
    credits: { enabled: false },
    tooltip: { shared: true, headerFormat: '' },
    accessibility: { enabled: false },
  });
  addStockChartModule(Highcharts);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const chartComponent = useRef<any>(null);
  const [smp, setSmp] = useState<SmpData[]>([]);
  const [option, setOption] = useState<Highcharts.Options>(defaultOption);

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    convertToChartData();
  }, [smp]);

  function fetch() {
    ImplwayService.getInstance().getSmpDaily()
    .then(response => {
      setIsLoading(false);
      const smpList: SmpData[] = [];
      if (response && ResponseResultCode.Y === response.resultCode) {
        response.data?.forEach((f: SmpModel) => {
          smpList.push({
            trdYmd: f.trdYmd ?? '',
            areaCd: Number(f.areaCd!),
            avgSmp: f.avgSmp ?? 0,
          });
        });
      }
      setSmp(smpList);
    })
    .catch(() => {
      setIsLoading(false);
      setSmp([]);
    });
  }

  function convertToChartData() {
    const smpInLand = smp.filter(value => 1 === value.areaCd);
    const smpJeju = smp.filter(value => 9 === value.areaCd);

    setOption({
      ...defaultOption,
      series: [{
        type: 'line',
        name: '육지',
        color: '#74D9B6',
        lineWidth: 2,
        data: smpInLand.map(m => [dayjs(m.trdYmd, Constant.FORMAT_YMD_ALT).unix() * 1000, m.avgSmp]),
      }, {
        type: 'line',
        name: '제주',
        color: '#61C3D5',
        lineWidth: 2,
        data: smpJeju.map(m => [dayjs(m.trdYmd, Constant.FORMAT_YMD_ALT).unix() * 1000, m.avgSmp]),
      }]
    });
  }

  return (
    <DashboardCard
      title="SMP"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/mng/mng004"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      <div className="chart">
        {option && (
          <HighchartsReact
            highcharts={Highcharts}
            options={option}
            constructorType="stockChart"
            ref={chartComponent}
          />
        )}
      </div>
    </DashboardCard>
  );
}